import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge, ListGroup } from 'react-bootstrap';
import { downloadPreferenceTemplate, uploadStudentPreferences } from '../../api/preferenceApi';
import { toast } from 'react-toastify';

const UploadStudentPreferencesModal = ({ show, onHide, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx')) {
        setError('Please select a valid Excel (.xlsx) file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setUploadResults(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      await downloadPreferenceTemplate();
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const response = await uploadStudentPreferences(file);
      
      if (response.results) {
        setUploadResults(response.results);
        
        // Show success message
        const { users_created, students_created, preferences_created, duplicates_skipped } = response.results;
        let message = `Upload successful! Created ${users_created} users, ${students_created} students, and ${preferences_created} preferences.`;
        if (duplicates_skipped > 0) {
          message += ` Skipped ${duplicates_skipped} duplicate emails.`;
        }
        toast.success(message);
        
        // Call onSuccess callback if no errors
        if (response.results.errors.length === 0) {
          if (onSuccess) {
            onSuccess();
          }
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.response?.data?.message || 'Failed to upload file');
      toast.error('Upload failed. Please check the error message.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResults(null);
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Upload Student Preferences</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <Alert.Heading>Instructions</Alert.Heading>
          <ol className="mb-0">
            <li>Download the Excel template using the button below</li>
            <li>Fill in student emails in the Email column</li>
            <li>For each company, enter preference order (1, 2, 3...) or "no" if not preferred</li>
            <li>Upload the completed Excel file</li>
          </ol>
        </Alert>

        <div className="mb-3">
          <Button 
            variant="outline-primary" 
            onClick={handleDownloadTemplate}
            disabled={downloading}
            className="w-100"
          >
            {downloading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Downloading Template...
              </>
            ) : (
              '📥 Download Excel Template'
            )}
          </Button>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Select Excel File to Upload</Form.Label>
          <Form.Control
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && (
            <Form.Text className="text-success">
              Selected: {file.name}
            </Form.Text>
          )}
        </Form.Group>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {uploadResults && (
          <Alert variant={uploadResults.errors.length > 0 ? 'warning' : 'success'}>
            <Alert.Heading>Upload Results</Alert.Heading>
            <div className="mb-2">
              <Badge bg="primary" className="me-2">
                Users Created: {uploadResults.users_created}
              </Badge>
              <Badge bg="success" className="me-2">
                Students Created: {uploadResults.students_created}
              </Badge>
              <Badge bg="info" className="me-2">
                Preferences Created: {uploadResults.preferences_created}
              </Badge>
              {uploadResults.duplicates_skipped > 0 && (
                <Badge bg="warning">
                  Duplicates Skipped: {uploadResults.duplicates_skipped}
                </Badge>
              )}
            </div>
            
            {uploadResults.errors.length > 0 && (
              <>
                <hr />
                <h6>Errors ({uploadResults.errors.length}):</h6>
                <ListGroup variant="flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {uploadResults.errors.map((error, idx) => (
                    <ListGroup.Item key={idx} className="px-0 py-1">
                      <small className="text-danger">{error}</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={handleUpload}
          disabled={!file || uploading}
        >
          {uploading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Uploading...
            </>
          ) : (
            'Upload File'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadStudentPreferencesModal;